import { supabase } from '@/core/database/supabase';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';
import {
  flattenPermissionCatalog,
  getConflictWarnings,
  getDependencyWarnings,
  getPermissionLabel,
  getPermissionMeta,
  normalizePermissionsJson,
  permissionCatalog,
  permissionJsonToKeys,
  permissionKeysToJson,
} from '../../catalog/permissionCatalog';
import type {
  EffectivePermissionGroup,
  PermissionAssignment,
  PermissionAuditLog,
  PermissionImpact,
  PermissionRole,
  PermissionStats,
  PermissionUser,
} from '../types/permissionsList.types';

type Row = Record<string, any>;

function mapRole(role: Row): PermissionRole {
  const permissionsJson = normalizePermissionsJson(role.permissions_json);
  return {
    id: String(role.id),
    code: String(role.code || ''),
    name: String(role.name || role.code || ''),
    isSystem: Boolean(role.is_system),
    permissionsJson,
    permissionKeys: permissionJsonToKeys(permissionsJson),
  };
}

function mapUser(user: Row): PermissionUser {
  return {
    id: String(user.id),
    fullName: String(user.full_name || user.email || 'Người dùng'),
    email: String(user.email || ''),
    roleCode: String(user.role_code || ''),
    jobTitle: String(user.job_title || ''),
    isActive: Boolean(user.is_active ?? true),
  };
}

function getUserRoleIds(user: PermissionUser, roles: PermissionRole[], assignments: PermissionAssignment[]) {
  const primaryRoleId = roles.find((role) => role.code === user.roleCode)?.id;
  return new Set([
    ...(primaryRoleId ? [primaryRoleId] : []),
    ...assignments.filter((assignment) => assignment.userId === user.id).map((assignment) => assignment.roleId),
  ]);
}

function extractStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export const permissionsListService = {
  getModules() {
    return permissionCatalog;
  },

  async getRoles(): Promise<PermissionRole[]> {
    await enforceSettingsPermission('settings.permissions.view');
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .order('name');

    if (error) throw error;
    return (data || []).map(mapRole);
  },

  async getUsers(companyId: string): Promise<PermissionUser[]> {
    await enforceSettingsPermission('settings.permissions.view', { companyId });
    let query = supabase
      .from('user_profiles')
      .select('id, full_name, email, role_code, job_title, is_active')
      .order('full_name');

    if (companyId) query = query.eq('company_id', companyId);
    const { data, error } = await query;

    if (error) throw error;
    return (data || []).map(mapUser);
  },

  async getAssignments(): Promise<PermissionAssignment[]> {
    const { data, error } = await supabase
      .from('user_roles')
      .select('user_id, roles!inner(id)');

    if (error) return [];
    return (data || []).map((assignment: any) => {
      const rId = Array.isArray(assignment.roles) ? assignment.roles[0]?.id : assignment.roles?.id;
      return {
        userId: String(assignment.user_id || ''),
        roleId: String(rId || ''),
      };
    }).filter((assignment) => assignment.userId && assignment.roleId);
  },

  buildImpact(
    role: PermissionRole,
    permissionKey: string,
    nextEnabled: boolean,
    users: PermissionUser[],
    roles: PermissionRole[],
    assignments: PermissionAssignment[],
  ): PermissionImpact {
    const meta = getPermissionMeta(permissionKey);
    const dependencies = nextEnabled ? (meta?.action.dependencies || []) : [];
    return {
      role,
      permissionKey,
      permissionLabel: getPermissionLabel(permissionKey),
      nextEnabled,
      affectedUsers: users.filter((user) => getUserRoleIds(user, roles, assignments).has(role.id)),
      dependencyWarnings: getDependencyWarnings(permissionKey, role.permissionKeys, nextEnabled),
      conflictWarnings: nextEnabled ? getConflictWarnings(permissionKey, role.permissionKeys) : [],
      autoIncludedDependencies: dependencies.filter((dependency) => !role.permissionKeys.includes(dependency)),
      risk: meta?.action.risk || 'Không rõ',
      scopes: meta?.action.scopes || ['Công ty'],
    };
  },

  async setRolePermission(input: {
    role: PermissionRole;
    permissionKey: string;
    enabled: boolean;
    actorUserId?: string | null;
    affectedUsers: PermissionUser[];
  }) {
    await enforceSettingsPermission('settings.permissions.manage');
    const currentKeys = new Set(input.role.permissionKeys);
    const meta = getPermissionMeta(input.permissionKey);

    if (input.enabled) {
      currentKeys.add(input.permissionKey);
      (meta?.action.dependencies || []).forEach((dependency) => currentKeys.add(dependency));
    } else {
      currentKeys.delete(input.permissionKey);
    }

    const permissionsJson = permissionKeysToJson(Array.from(currentKeys));
    const { error } = await supabase
      .from('roles')
      .update({
        permissions_json: permissionsJson,
        updated_at: new Date().toISOString(),
      })
      .eq('id', input.role.id);

    if (error) throw error;

    const summary = `${input.enabled ? 'Bật' : 'Tắt'} quyền ${getPermissionLabel(input.permissionKey)} cho vai trò ${input.role.name}`;
    const { error: auditError } = await supabase.from('audit_logs').insert({
      actor_user_id: input.actorUserId || null,
      action_code: input.enabled ? 'PERMISSION_ENABLE' : 'PERMISSION_DISABLE',
      module_code: 'permissions',
      entity_type: 'role_permission',
      entity_id: input.role.id,
      summary,
      before_data: {
        roleCode: input.role.code,
        permissionKeys: input.role.permissionKeys,
      },
      after_data: {
        roleCode: input.role.code,
        permissionKey: input.permissionKey,
        permissionLabel: getPermissionLabel(input.permissionKey),
        permissionKeys: Array.from(currentKeys),
        autoIncludedDependencies: meta?.action.dependencies || [],
        affectedUserIds: input.affectedUsers.map((user) => user.id),
      },
    });

    if (auditError) console.warn('[permissions] audit log fallback:', auditError);
  },

  async rollbackPermissionLog(log: PermissionAuditLog, actorUserId?: string | null) {
    await enforceSettingsPermission('settings.permissions.manage');
    if (!log.canRollback || !log.entityId) throw new Error('Nhật ký này không thể khôi phục');
    const permissionsJson = permissionKeysToJson(log.beforePermissionKeys);
    const { error } = await supabase
      .from('roles')
      .update({
        permissions_json: permissionsJson,
        updated_at: new Date().toISOString(),
      })
      .eq('id', log.entityId);

    if (error) throw error;

    const { error: auditError } = await supabase.from('audit_logs').insert({
      actor_user_id: actorUserId || null,
      action_code: 'PERMISSION_ROLLBACK',
      module_code: 'permissions',
      entity_type: 'role_permission',
      entity_id: log.entityId,
      summary: `Khôi phục thay đổi quyền từ nhật ký ${log.id}`,
      before_data: { permissionKeys: log.afterPermissionKeys },
      after_data: { permissionKeys: log.beforePermissionKeys, rollbackLogId: log.id },
    });

    if (auditError) console.warn('[permissions] rollback audit fallback:', auditError);
  },

  async getRecentAuditLogs(): Promise<PermissionAuditLog[]> {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('id, entity_id, summary, action_code, before_data, after_data, created_at, actor:user_profiles!actor_user_id(full_name)')
      .eq('module_code', 'permissions')
      .eq('entity_type', 'role_permission')
      .order('created_at', { ascending: false })
      .limit(8);

    if (error) return [];

    return (data || []).map((log: Row) => {
      const beforeKeys = extractStringArray(log.before_data?.permissionKeys);
      const afterKeys = extractStringArray(log.after_data?.permissionKeys);
      return {
        id: String(log.id),
        entityId: String(log.entity_id || ''),
        summary: String(log.summary || ''),
        actionCode: String(log.action_code || ''),
        roleName: String(log.after_data?.roleCode || log.before_data?.roleCode || ''),
        permissionLabel: String(log.after_data?.permissionLabel || ''),
        actorName: String(log.actor?.full_name || 'Hệ thống'),
        createdAt: log.created_at || null,
        beforePermissionKeys: beforeKeys,
        afterPermissionKeys: afterKeys,
        canRollback: Boolean(log.entity_id && beforeKeys.length > 0 && afterKeys.length > 0),
      };
    });
  },

  getEffectivePermissionGroups(
    user: PermissionUser | null,
    roles: PermissionRole[],
    assignments: PermissionAssignment[],
  ): EffectivePermissionGroup[] {
    if (!user) return [];
    const isAdmin = user.roleCode === 'super_admin' || user.roleCode === 'admin';
    const roleIds = getUserRoleIds(user, roles, assignments);
    const userRoles = roles.filter((role) => roleIds.has(role.id));
    
    if (!userRoles.length && !isAdmin) return [];

    const groups = new Map<string, EffectivePermissionGroup>();
    const roleNamesByPermission = new Map<string, Set<string>>();

    if (isAdmin) {
      // Nếu là Admin, gán tất cả quyền từ catalog
      flattenPermissionCatalog().forEach((item) => {
        const names = new Set<string>(['Quyền quản trị hệ thống']);
        roleNamesByPermission.set(item.key, names);
      });
    }

    userRoles.forEach((role) => {
      role.permissionKeys.forEach((key) => {
        const names = roleNamesByPermission.get(key) || new Set<string>();
        names.add(role.name);
        roleNamesByPermission.set(key, names);
      });
    });

    roleNamesByPermission.forEach((roleNames, key) => {
      const meta = getPermissionMeta(key);
      const moduleName = meta?.module.name || key.split('.')[0] || 'Khác';
      const group = groups.get(moduleName) || { moduleName, permissions: [] };
      group.permissions.push({
        key,
        label: getPermissionLabel(key),
        risk: meta?.action.risk || 'Không rõ',
        scopes: meta?.action.scopes || ['Công ty'],
        roleNames: Array.from(roleNames),
      });
      groups.set(moduleName, group);
    });

    return Array.from(groups.values());
  },

  getStats(roles: PermissionRole[], users: PermissionUser[], assignments: PermissionAssignment[]): PermissionStats {
    const catalog = flattenPermissionCatalog();
    const highRiskKeys = new Set(
      catalog.filter((item) => item.action.risk === 'Cao' || item.action.risk === 'Rất cao').map((item) => item.key),
    );
    const enabledKeys = roles.flatMap((role) => role.permissionKeys);
    return {
      totalPermissions: catalog.length,
      enabledCells: enabledKeys.length,
      highRiskCells: enabledKeys.filter((key) => highRiskKeys.has(key)).length,
      rolesCount: roles.length,
      usersCovered: users.filter((user) => getUserRoleIds(user, roles, assignments).size > 0).length,
    };
  },
};
